import type { Toast } from 'react-hot-toast'

import cx from 'classnames'
import { isValid } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useToaster } from 'react-hot-toast'
import { MdChevronLeft, MdClose, MdPerson } from 'react-icons/md'
import { animated, config, useSpring } from 'react-spring'

import { formatLastMessageDateTime } from '../chats'

interface Action {
  label: string
  onClick: () => void
}

export interface NotificationProps extends Pick<Toast, 'id' | 'visible'> {
  title?: string
  date?: Date
  children: React.ReactNode
  left?: React.ReactNode
  right?: React.ReactNode
  actions?: Action[]
  enableActionsDropdown?: boolean
  isPatient?: boolean
  onClick?: () => void
  onDismiss: () => void
}

export function Notification ({ id, isPatient = false, visible, title, date, left: Left, right: Right, children, actions, enableActionsDropdown = true, onClick, onDismiss, ...rest }: NotificationProps): JSX.Element {
  let { toasts } = useToaster()

  let ref = useRef<HTMLDivElement>(null)

  let [ isHovering, setIsHovering ] = useState(false)
  let [ showActions, setShowActions ] = useState(false)

  let [ style, api ] = useSpring(() => ({
    from: {
      opacity: 0,
      transform: 'translate(4rem, 0rem) scale(1)'
    },
    to: {
      opacity: 1,
      transform: 'translate(0rem, 0rem) scale(1)'
    }
  }), [ visible ])

  let attachmentStyle = useSpring({
    opacity: isHovering
      ? 0
      : 1
  })

  let controlsStyle = useSpring({
    transform: isHovering
      ? 'translate(0rem, 0rem)'
      : 'translate(2.25rem, 0rem)',

    opacity: isHovering
      ? 1
      : 0
  })

  let optionsButtonStyle = useSpring({
    transform: isHovering || showActions
      ? 'translate(0rem, 0rem)'
      : 'translate(0rem, 0.5rem)',

    opacity: isHovering || showActions
      ? 1
      : 0
  })

  let actionsStyle = useSpring({
    transform: showActions
      ? 'scale(1.00) translate(0rem, calc(100% - 0rem))'
      : 'scale(0.95) translate(0rem, calc(100% - 0.25rem))',

    opacity: showActions
      ? 1
      : 0,

    config: config.stiff
  })

  let index = toasts.findIndex(toast => toast.id === id)
  let zIndex = 999999 + toasts.length - index

  useEffect(() => {
    if (ref.current?.parentElement) {
      ref.current.parentElement.style.zIndex = zIndex.toString()
    }
  }, [ zIndex ])

  useEffect(() => {
    if (!isHovering) {
      setShowActions(false)
    }
  }, [ isHovering ])

  useEffect(() => {
    if (!visible) {
      api.start({
        opacity: 0,
        transform: 'translate(0rem, 0.5rem) scale(0.75)'
      })
    }
  }, [ visible, api ])

  let Wrapper = typeof onClick === 'function'
    ? animated.button
    : animated.div

  return (
    <animated.div
      className='relative font-inter w-80 backdrop-blur-sm'
      ref={ref}
      style={{
        ...style,
        zIndex
      }}
      onMouseEnter={() => {
        setIsHovering(true)
      }}
      onMouseLeave={() => {
        setIsHovering(false)
      }}
    >
      <Wrapper
        className='notification border border-[#6b6b6c] font-inter font-normal text-base p-3 rounded-xl bg-[#2f2e34] bg-opacity-75 text-white w-full shadow-lg ring-[#5e5e5f] ring-1 flex flex-row justify-start items-start gap-3 will-change-transform relative select-none overflow-hidden'
        onClick={onClick}
        data-test-id={`notification-${id}`}
      >
        {Left}

        <div className='flex flex-col flex-grow justify-start items-start gap-[2px]'>
          <div
            className={cx(
              'font-inter text-sm font-medium -mt-[3px]',
              isPatient && 'text-[#dfa450] font-semibold'
            )}
          >
            {title}

            {isPatient && (
              <MdPerson
                size={18}
                className='inline-block text-inherit -translate-y-[1px] cursor-pointer ml-1'
              />
            )}
          </div>

          <div className='flex flex-row gap-4 align-bottom justify-between w-full'>
            <p className='font-inter text-sm font-normal line-clamp-3'>
              {children}
            </p>

            {date && isValid(date) && (
              <span className='text-xs self-end font-inter -translate-y-[1px]'>
                {formatLastMessageDateTime(date)}
              </span>
            )}
          </div>

          {/* Hello??? */}
        </div>

        {Right && (
          <animated.div
            className='w-9 aspect-square rounded-md overflow-hidden pointer-events-none flex-shrink-0'
            style={attachmentStyle}
          >
            {Right}
          </animated.div>
        )}
      </Wrapper>

      {Array.isArray(actions) && actions.length > 0 && enableActionsDropdown && (
        <div
          className={cx(
            'absolute bottom-1.5 right-1.5 transition-opacity',
            !isHovering && 'pointer-events-none',
            isHovering && 'pointer-events-auto'
          )}
        >
          <animated.button
            type='button'
            className='pl-2.5 pr-1.5 py-1.5 rounded-lg bg-[#3c3b41] text-xs font-inter text-[#dfdfe0] hover:bg-[#424148] transition-colors'
            onClick={() => {
              setShowActions(showActions => !showActions)
            }}
            style={optionsButtonStyle}
          >
            Options
            {' '}
            <MdChevronLeft
              size={14}
              className='inline-block text-inherit -rotate-90 pointer-events-none cursor-pointer'
            />
          </animated.button>

          <animated.div
            className={cx(
              'absolute bottom-0 right-0 p-1.5 flex flex-col bg-[#606061] border border-[#7c7d7d] ring-[#646565] ring-1 rounded-md min-w-[125%]',
              showActions && 'pointer-events-auto',
              !showActions && 'pointer-events-none'
            )}
            style={actionsStyle}
          >
            {actions.map((action) => (
              <button
                key={action.label}
                type='button'
                className='px-2 py-[3px] font-inherit text-xs text-[#ebebeb] bg-transparent hover:bg-[#5b8fe1] hover:text-white transition-colors rounded whitespace-nowrap'
                onClick={() => {
                  setShowActions(false)

                  setTimeout(() => {
                    action.onClick()
                  }, 125)
                }}
              >
                {action.label}
              </button>
            ))}
          </animated.div>
        </div>
      )}

      {Array.isArray(actions) && actions.length > 0 && !enableActionsDropdown && (
        <div
          className={cx(
            'absolute bottom-1.5 right-1.5 transition-opacity',
            !isHovering && 'pointer-events-none',
            isHovering && 'pointer-events-auto'
          )}
        >
          <animated.div
            className='flex flex-row gap-1.5'
            style={optionsButtonStyle}
          >
            {actions.map((action) => (
              <button
                key={action.label}
                type='button'
                className='px-2.5 py-1.5 rounded-lg bg-[#3c3b41] text-xs font-inter text-[#dfdfe0] hover:bg-[#424148] transition-colors'
                onClick={() => {
                  setShowActions(false)

                  setTimeout(() => {
                    action.onClick()
                  }, 125)
                }}
              >
                {action.label}
              </button>
            ))}
          </animated.div>
        </div>
      )}

      <animated.div
        className='flex flex-col justify-between items-end absolute top-0 right-0'
        style={controlsStyle}
        onMouseEnter={() => {
          setIsHovering(true)
        }}
      >
        <button
          type='button'
          className={cx(
            'p-1.5 transition-opacity',
            isHovering && 'opacity-25 hover:opacity-100',
            !isHovering && 'opacity-0 hover:opacity-75'
          )}
          onClick={(event) => {
            event.stopPropagation()
            onDismiss()
          }}
        >
          <MdClose
            size={16}
            className='text-white'
          />
        </button>
      </animated.div>
    </animated.div>
  )
}
